import React from 'react';

/** Dónde se clava: `-1px` cuando no hay barra superior, `55px` debajo de ella. */
const ANCLAJE_MOVIL = 55;
const ANCLAJE_ESCRITORIO = -1;
/** El mismo corte que `lg:` en Tailwind, que es donde desaparece la barra del móvil. */
const CORTE_LG = 1024;

const anclajeActual = () =>
    (typeof window !== 'undefined' && window.innerWidth >= CORTE_LG)
        ? ANCLAJE_ESCRITORIO
        : ANCLAJE_MOVIL;

/**
 * La fila que SIEMPRE se queda a la vista, también en el teléfono.
 *
 * Es un marcador: quien clava es `<CabeceraFija>`, que la saca a hermana del
 * resto de la cabecera. Ponerle el `sticky` aquí dentro era el fallo original
 * —`sticky` solo pega mientras la caja del PADRE está a la vista, y el padre
 * era la cabecera, que mide 300 px—, así que la fila se despegaba en cuanto la
 * cabecera salía de pantalla. Se veía como que la app «lo intenta y en cierto
 * punto ya no se queda clavado».
 *
 * Va donde tenga que ir dentro de la cabecera: lo que esté ENCIMA se va al
 * hacer scroll y lo que esté DEBAJO pasa por detrás. En Clientes es la fila de
 * pestañas, que va primera. En una vista con título propio conviene que sea el
 * buscador, porque el título ya lo repite la barra superior del teléfono.
 */
export const FilaFija: React.FC<{
    children: React.ReactNode;
    className?: string;
}> = ({ children, className = '' }) => (
    <div className={className}>{children}</div>
);

/** ¿Este hijo es la fila que se queda? */
function esFilaFija(nodo: React.ReactNode): boolean {
    return React.isValidElement(nodo) && nodo.type === FilaFija;
}

/**
 * Avisa cuando el riel ya tocó su tope, para poder dibujarle una línea debajo.
 *
 * Solo cambia una sombra, nunca el alto. Es a propósito: una versión anterior
 * plegaba la cabecera de verdad, y como lo que encogía estaba ARRIBA del punto
 * donde estabas leyendo, el navegador recolocaba el scroll para compensar
 * (scroll anchoring) — la cabecera se quedaba trabada en compacta y la lista
 * pegaba un brinco. Aquí no se mueve ni un píxel de layout.
 */
function useClavado(caja: React.RefObject<HTMLElement | null>): boolean {
    const [clavado, setClavado] = React.useState(false);

    React.useEffect(() => {
        const el = caja.current;
        if (!el) return;
        let pedido = 0;
        const mirar = () => {
            pedido = 0;
            const anclaje = parseFloat(getComputedStyle(el).top) || 0;
            setClavado(el.getBoundingClientRect().top <= anclaje + 1);
        };
        const alScroll = () => {
            if (pedido) return;
            pedido = requestAnimationFrame(mirar);
        };
        mirar();
        window.addEventListener('scroll', alScroll, { passive: true });
        window.addEventListener('resize', alScroll);
        return () => {
            if (pedido) cancelAnimationFrame(pedido);
            window.removeEventListener('scroll', alScroll);
            window.removeEventListener('resize', alScroll);
        };
    }, [caja]);

    return clavado;
}

/** Sangrado lateral: iguala el padding del contenedor de páginas de App.tsx. */
const A_LOS_BORDES = '-mx-4 px-4 sm:-mx-6 sm:px-6 md:-mx-8 md:px-8';

/**
 * `lg:-mt-[25px] lg:pt-[42px]` — la banda ARRANCA ya clavada.
 *
 * Se reportó como «se mueve un poco al hacer scroll», y no era un temblor: la
 * banda subía 25 px y ahí se quedaba. Es lo normal de `sticky` —el elemento
 * viaja con la página hasta alcanzar su `top`— pero encima había el `py-6` del
 * armazón, así que ese viaje se veía.
 *
 * El margen negativo sube la banda hasta su punto de anclaje (−1px) y el
 * padding devuelve el hueco por dentro: se ve igual, pero no le queda nada que
 * recorrer. Cero movimiento desde el primer píxel.
 *
 * Solo desde `lg`. Debajo hay una barra superior propia (h-14, `lg:hidden`) que
 * ocupa sitio en el flujo, y subir la banda la metería detrás de ella.
 */
const ARRANQUE = 'pt-[calc(0.5rem+1px)] lg:-mt-[25px] lg:pt-[42px]';

/**
 * La banda de arriba de una sección: título, KPIs y pestañas, clavados.
 *
 * ── Por qué `sticky` y no una caja de alto fijo ─────────────────────────────
 *
 * La tentación es fijar la vista al alto de la ventana (`h-full`) y meter la
 * lista en una caja con scroll propio. Se ve igual y está roto: con el ratón
 * encima de la tarjeta blanca la rueda mueve ESA caja, no la página. Y como
 * `.custom-scrollbar` lleva `overscroll-behavior: contain` (index.css), al
 * llegar al final del panel el gesto tampoco se pasa a la página — hay que
 * sacar el cursor al gris de los lados para poder bajar. Se reportó tal cual:
 * «en el recuadro blanco no funciona el scroll».
 *
 * `sticky` clava sin crear una caja con scroll. La que se mueve sigue siendo la
 * página, así que la rueda funciona esté donde esté el cursor.
 *
 * ── En el teléfono: un riel que no se suelta ────────────────────────────────
 *
 * En una pantalla de 390 px la cabecera entera —pestañas, KPIs y buscador— se
 * apila y ocupa media pantalla: clavarla completa no dejaría lista que leer.
 * Así que se clava solo la fila que la vista marcó con `<FilaFija>`, y el resto
 * se va al hacer scroll.
 *
 * La clave está en CÓMO. La fila no se renderiza dentro de la cabecera sino
 * como HERMANA suya, porque `sticky` solo pega mientras la caja de su padre
 * sigue a la vista: dentro de la cabecera su padre medía 300 px y se despegaba
 * ahí mismo. Como hermana, su padre es la vista entera y aguanta hasta el
 * último renglón de la lista.
 *
 * Y no se pliega nada. Plegar la parte alta cambiaba el alto del documento por
 * ENCIMA de donde estabas leyendo, el navegador recolocaba el scroll para
 * compensar y la lista pegaba un brinco. Aquí lo que sobra simplemente pasa por
 * debajo del riel y desaparece, que es lo que hace una cabecera de iOS.
 *
 * Desde `md` los tres bloques se clavan uno debajo del otro y se ven
 * exactamente como la banda única de antes.
 *
 * Sin `<FilaFija>` no hay forma de saber qué merece quedarse, así que la banda
 * se comporta como siempre: una sola caja, clavada desde `md` y estática en el
 * teléfono.
 *
 * ── Por qué se anula el hueco del contenedor ────────────────────────────────
 *
 * Al partirse en tres, los bloques pasan a ser hijos de la vista y heredan su
 * `gap`. Ese hueco los separa en el flujo, y entonces el segundo bloque nace
 * unos píxeles por DEBAJO de su punto de anclaje: al bajar los recorre y se ve
 * moverse — el mismo «se mueve un poco» que costó arreglar. Se mide el `gap`
 * real del padre y se anula con un margen negativo, así los bloques quedan
 * pegados igual que cuando eran una sola caja, y el hueco visible lo pone el
 * padding de cada bloque.
 *
 * ── El fondo tiene que ser el MISMO hex de la página ────────────────────────
 *
 * `bg-acero-100` es el color que pinta el fondo de la app (#ECEFF5, App.tsx).
 * Con `acero-50` —un 4 % más claro— la banda se veía como un rectángulo pálido
 * flotando sobre el gris, con las tarjetas blancas disolviéndose dentro. Mismo
 * hex y opaco: la banda no se ve, solo se nota que no se mueve.
 *
 * Ya clavado, el riel lleva además una línea abajo. Sin ella, la tarjeta blanca
 * que le pasa por debajo se ve asomar contra el mismo gris y parece un trozo
 * suelto de interfaz — es justo lo que se veía roto en el teléfono. La línea lo
 * convierte en una capa que está encima. Es una sombra, no un borde: no ocupa
 * sitio, así que aparecer no mueve nada.
 *
 * ── El píxel de arriba ──────────────────────────────────────────────────────
 *
 * `top-[-1px]` con un píxel más de padding. Con la banda a `top: 0` exacto, en
 * pantallas con escalado no entero (125 %, 150 % en Windows) el redondeo deja
 * asomar una línea de contenido por encima en algunos desplazamientos, y se lee
 * como que la cabecera «tiembla». Un píxel de sangrado lo tapa sin moverla.
 */
export const CabeceraFija: React.FC<{
    children: React.ReactNode;
    className?: string;
}> = ({ children, className = '' }) => {
    const primero = React.useRef<HTMLDivElement>(null);
    const riel = React.useRef<HTMLDivElement>(null);
    const ultimo = React.useRef<HTMLDivElement>(null);

    const hijos = React.Children.toArray(children);
    const corte = hijos.findIndex(esFilaFija);
    const conRiel = corte >= 0;
    const antes = conRiel ? hijos.slice(0, corte) : [];
    const despues = conRiel ? hijos.slice(corte + 1) : [];

    const clavado = useClavado(riel);

    /* Cada bloque publica dónde acaba, para que el siguiente sepa dónde clavarse
     * y para que el resto de la pantalla sepa dónde empieza el contenido.
     *
     * `--rp-cabecera` hace falta porque la banda no es la única pieza clavada:
     * en Gastos fijos hay además una tarjeta lateral («Los pasos») que también
     * es `sticky`. Iba a `top: 1rem`, o sea que se clavaba DETRÁS de la banda y
     * su propio título quedaba tapado.
     *
     * Se mide en vez de escribir los números a mano porque el alto cambia: en
     * Estimados la banda lleva KPIs, buscador y filtros; en Gastos fijos solo
     * las pestañas. Un `top-20` fijo acertaría hoy en una pestaña y fallaría en
     * la otra, y volvería a fallar en cuanto alguien añada una fila.
     *
     * `ResizeObserver` y no una medición al montar: el alto cambia al cambiar
     * de pestaña, al redimensionar y cuando las pestañas pasan a dos renglones
     * en un teléfono. */
    React.useLayoutEffect(() => {
        const cabeza = primero.current ?? riel.current;
        if (!cabeza) return;
        /* Las variables se escriben en el PADRE, no en la raíz del documento.
         *
         * Estaban en la raíz y eso rompió Estimados ▸ Firmadas: ahí conviven DOS
         * cabeceras —la de Estimados, con sus pestañas, y la de la pantalla de
         * Firmadas—, y la segunda en montarse pisaba la variable de la primera.
         * El riel de pestañas se anclaba entonces a la altura de la otra
         * cabecera y quedaba detrás de ella: desaparecía.
         *
         * Las custom properties heredan, así que ponerlas en el padre las hace
         * llegar igual a todo lo que cuelga de la vista —`BajoCabecera`, la
         * cabecera de tabla, la tarjeta de «Los pasos»— y de paso cada cabecera
         * manda solo dentro de su propio subárbol. Dos en la misma pantalla
         * dejan de estorbarse, y ya no hace falta anotar quién era el dueño
         * para limpiar. */
        const raiz = cabeza.parentElement;
        if (!raiz) return;

        /* El BORDE INFERIOR ya clavado, no el alto a secas. No es lo mismo: la
           banda se ancla a `-1px` en escritorio pero a `55px` en tablet, debajo
           de la barra superior. Publicando solo el alto, el cálculo salía 55 px
           corto justo ahí y la tarjeta se metía detrás. */
        const bordeDe = (el: HTMLElement) =>
            (parseFloat(getComputedStyle(el).top) || 0) + el.offsetHeight;
        const clavable = (el: HTMLElement | null): el is HTMLElement =>
            !!el && getComputedStyle(el).position === 'sticky';

        /* ¿Hay OTRA cabecera clavada por encima de esta?
         *
         * En Estimados ▸ Firmadas hay dos: la de Estimados con sus pestañas, y
         * la de la propia pantalla de Firmadas, que se renderiza dentro del
         * contenido de la primera. Las dos se anclaban arriba del todo, y como
         * la banda es opaca, la de dentro tapaba entera a la de fuera: la fila
         * de pestañas «desaparecía» al entrar a Firmadas.
         *
         * Se lee del ABUELO a propósito. Esta cabecera escribe sus variables en
         * su padre, así que preguntarle al padre devolvería su propio valor y el
         * cálculo se perseguiría a sí mismo. El abuelo solo puede tener el valor
         * de una cabecera de fuera. */
        const anclajeExterior = () => {
            const fuera = raiz.parentElement;
            if (!fuera) return anclajeActual();
            const heredado = parseFloat(getComputedStyle(fuera).getPropertyValue('--rp-cabecera'));
            return Number.isFinite(heredado) ? heredado : anclajeActual();
        };

        /* Los `top` se calculan aquí y se escriben en línea, no con clases.
         *
         * Encadenarlos con `top-[var(--rp-antes)]` funcionaba con una sola
         * cabecera y se rompía con dos, porque la variable es de todo el
         * subárbol y el orden de montaje decidía quién ganaba. Un número
         * calculado no depende de quién se montó primero. */
        const medir = () => {
            const base = anclajeExterior();

            // El bloque de arriba solo se clava desde `md`; en el teléfono se va,
            // así que el riel se ancla directo bajo la barra superior.
            const arriba = primero.current;
            if (arriba) arriba.style.top = `${base}px`;
            const finArriba = clavable(arriba) ? bordeDe(arriba) : base;

            const enRiel = riel.current;
            if (enRiel) enRiel.style.top = `${finArriba}px`;
            const finRiel = enRiel ? bordeDe(enRiel) : finArriba;

            const abajo = ultimo.current;
            if (abajo) abajo.style.top = `${finRiel}px`;

            raiz.style.setProperty(
                '--rp-cabecera',
                clavable(abajo) ? `${finRiel + abajo.offsetHeight}px` : `${finRiel}px`,
            );
        };

        /* Se anula el `gap` del contenedor para que los bloques queden pegados
           en el flujo. Se MIDE en vez de suponerlo: Clientes usa `gap-5` y
           Estimados `gap-6`, y un número a ojo dejaría a uno de los dos con un
           salto al hacer scroll. */
        const pegarBloques = () => {
            const padre = cabeza.parentElement;
            if (!padre) return;
            const hueco = parseFloat(getComputedStyle(padre).rowGap) || 0;
            for (const el of [riel.current, ultimo.current]) {
                if (el && el !== cabeza) el.style.marginTop = `-${hueco}px`;
            }
        };

        pegarBloques();
        medir();

        const observador = new ResizeObserver(medir);
        for (const el of [primero.current, riel.current, ultimo.current]) {
            if (el) observador.observe(el);
        }
        const alRedimensionar = () => { pegarBloques(); medir(); };
        window.addEventListener('resize', alRedimensionar);
        return () => {
            observador.disconnect();
            window.removeEventListener('resize', alRedimensionar);
            raiz.style.removeProperty('--rp-cabecera');
        };
    }, [conRiel, antes.length, despues.length]);

    // Sin fila marcada: una sola caja, como siempre.
    if (!conRiel) {
        return (
            <div
                ref={primero}
                className={`md:sticky top-[calc(3.5rem-1px)] lg:top-[-1px] z-30 ${A_LOS_BORDES}
                    ${ARRANQUE} pb-3 bg-acero-100 flex flex-col gap-4 ${className}`}
            >
                {children}
            </div>
        );
    }

    const caja = `${A_LOS_BORDES} bg-acero-100 flex flex-col gap-4`;

    return (
        <>
            {antes.length > 0 && (
                <div
                    ref={primero}
                    className={`md:sticky top-[calc(3.5rem-1px)] lg:top-[-1px] z-30 ${caja} ${ARRANQUE} pb-4`}
                >
                    {antes}
                </div>
            )}

            <div
                ref={riel}
                className={`sticky top-[calc(3.5rem-1px)] lg:top-[-1px] z-30 ${caja}
                    ${antes.length === 0 ? `${ARRANQUE} ` : 'pt-0 '}${despues.length > 0 ? 'pb-4' : 'pb-3'}
                    ${clavado ? 'max-md:shadow-[0_1px_0_0_theme(colors.acero.200)]' : ''}`}
            >
                {hijos[corte]}
            </div>

            {despues.length > 0 && (
                <div
                    ref={ultimo}
                    className={`md:sticky z-20 ${caja} pt-0 pb-3 ${className}`}
                >
                    {despues}
                </div>
            )}
        </>
    );
};

/**
 * Se clava JUSTO DEBAJO de la banda.
 *
 * Para los buscadores y los filtros que viven dentro de un panel hijo y por eso
 * no pueden subir a la cabecera. Se apoya en `--rp-cabecera`, que la banda
 * publica con su borde inferior ya clavado, así que esto queda pegado sin
 * dejar hueco ni solaparse.
 *
 * Por qué también se clava: un buscador que se va al hacer scroll obliga a
 * subir hasta arriba cada vez que quieres afinar la búsqueda, y en una lista
 * de 790 materiales eso es justo lo que más se hace.
 *
 * `bg-acero-100` por defecto porque casi siempre va sobre el fondo de la
 * página; los que vivan dentro de una tarjeta blanca pasan `bg-white`.
 */
export const BajoCabecera: React.FC<{
    children: React.ReactNode;
    className?: string;
    /**
     * El relleno vertical, como prop y no por `className`.
     *
     * Dos utilidades de Tailwind para la misma propiedad no se resuelven por el
     * orden en que se escriben sino por el orden en que salen en la hoja, así
     * que pasar `pt-0` por `className` puede perder contra el `pt-2` de aquí y
     * el ajuste se «ignora» sin explicación. Con una prop no hay pelea.
     */
    relleno?: string;
    /**
     * Clavarla también en el teléfono. Solo para una fila corta —unas píldoras,
     * unos filtros—: con un título y su descripción dentro son 120 px de
     * pantalla que se pierden en un teléfono, encima de los 55 de la barra
     * superior y del riel de pestañas.
     */
    enTelefono?: boolean;
}> = ({ children, className = '', relleno = 'pt-2 pb-2.5', enTelefono = false }) => (
    <div
        className={`${enTelefono ? 'sticky top-[var(--rp-cabecera,55px)]' : 'md:sticky'}
            md:top-[var(--rp-cabecera,0px)] z-20 bg-acero-100
            ${A_LOS_BORDES} ${relleno} ${className}`}
    >
        {children}
    </div>
);

export default CabeceraFija;
